import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';
import {
  BuildFieldPathDialogComponent,
  BuildFieldPathDialogResult,
  type Hl7SegmentPickerOption,
} from '../dialogs/build-field-path-dialog.component';
import {
  collapseHl7SlashPredicateAttributeToFlatKey,
  type Hl7ObrObxLocateMeta,
} from '../hl7/hl7-field-key-map.flatten';
import {
  buildHl7PathFromSegmentPicker,
  hl7DisplayKeyFromPersistedPath,
} from '../hl7/hl7-path-step-builder';
import type { Hl7FieldOption } from '../models/field-option.model';
import type { RoutingPipelineBlock } from '../models/routing-ruleconfig1.models';
import { Hl7FieldCatalogService } from './hl7-field-catalog.service';
import {
  getRuleNameValidationMessage,
  RULE_NAME_ALREADY_EXISTS_MESSAGE,
  RuleEngineService,
} from '../../services/rule-engine.service';
import { RoutingRuleconfig1StoreService } from './routing-ruleconfig1-store.service';
import { routingExpressionPrep } from './routing-ruleconfig1-expression-prep.util';
import { isHl7PersistedFieldValue } from '../utils/routing-conversion-type.util';

@Injectable()
export class RoutingRuleconfig1Hl7RulesService {
  /** Bound to HL7 path inputs (mat-input syncs poorly with programmatic form patch). */
  hl7PickerSegment = '';
  hl7PickerFieldIndex = '';
  hl7PickerComponentIndex = '';

  readonly hl7IndexSelectOptions: number[] = Array.from({ length: 100 }, (_, i) => i);

  readonly filteredSegmentOptions$: Observable<Hl7FieldOption[]>;

  private readonly hl7SegmentFilter$ = new BehaviorSubject<string>('');
  private formSyncBound = false;
  private hl7BuildPathAutoOpenedForKey = '';
  private patchingHl7Form = false;

  /**
   * Wires segment autocomplete stream from catalog segment matches.
   * @example inject(RoutingRuleconfig1Hl7RulesService)
   */
  constructor(
    private readonly store: RoutingRuleconfig1StoreService,
    private readonly catalog: Hl7FieldCatalogService,
    private readonly dialog: MatDialog,
    private readonly toastr: ToastrService,
    private readonly ruleEngineService: RuleEngineService
  ) {
    this.filteredSegmentOptions$ = this.hl7SegmentFilter$.pipe(
      map((value) => this.getSegmentMatches((value ?? '').toString()))
    );
  }

  /**
   * Ensures HL7 catalog is loaded and form value-change sync is bound.
   * @example hl7Rules.ensureLoaded()
   */
  ensureLoaded(): void {
    this.catalog.ensureLoaded();
    this.bindHl7FormSync();
  }

  /**
   * After loading saved rules, maps persisted paths back to display keys (e.g. OBX.2.2).
   * @example hl7Rules.restoreFieldDisplayMappingsFromBlocks(pipelineBlocks)
   */
  restoreFieldDisplayMappingsFromBlocks(blocks: RoutingPipelineBlock[]): void {
    this.ensureLoaded();
    for (const [displayKey, path] of [...this.store.fieldDisplayToPath.entries()]) {
      if (displayKey === path || this.isFullHl7PersistedPath(displayKey)) {
        this.store.fieldDisplayToPath.delete(displayKey);
      }
    }
    for (const block of blocks) {
      for (const rule of block.rules ?? []) {
        const persisted = (rule.field ?? '').toString().trim();
        if (!persisted) {
          continue;
        }
        const display = this.toFieldDisplay(persisted);
        if (display && display !== persisted) {
          this.store.fieldDisplayToPath.set(display, this.trimFieldPath(persisted));
        }
      }
    }
  }

  /**
   * Normalizes field control value on focus and re-emits for mat-input sync.
   * @example hl7Rules.onFieldFocus()
   */
  onFieldFocus(): void {
    this.ensureLoaded();
    const normalized = this.normalizeFieldControlValue(this.store.fieldControl.value);
    if (normalized !== null) {
      this.store.fieldControl.setValue(normalized, { emitEvent: false });
    }
    this.store.fieldControl.setValue(this.store.fieldControl.value || '', { emitEvent: true });
  }

  /**
   * Delegates segment autocomplete filtering to the HL7 field catalog.
   * @example hl7Rules.getSegmentMatches('ms')
   */
  getSegmentMatches(value: string): Hl7FieldOption[] {
    this.ensureLoaded();
    return this.catalog.getSegmentMatches(value);
  }

  /**
   * Updates segment filter stream when the HL7 segment picker input changes.
   * @example hl7Rules.onHl7PickerSegmentChanged('OBR')
   */
  onHl7PickerSegmentChanged(value: string): void {
    this.hl7SegmentFilter$.next((value ?? '').toString());
  }

  /**
   * Refreshes segment autocomplete options when the segment input gains focus.
   * @example hl7Rules.onHl7SegmentInputFocus()
   */
  onHl7SegmentInputFocus(): void {
    this.hl7SegmentFilter$.next((this.hl7PickerSegment ?? '').toString());
  }

  /**
   * Hook when field/component index input gains focus (panel close handled in template).
   * @example hl7Rules.onHl7IndexFocus()
   */
  onHl7IndexFocus(): void {
    // Panel close is handled in the template via MatAutocompleteTrigger.
  }

  /**
   * Applies segment selection from autocomplete and syncs form + field control.
   * @example hl7Rules.onHl7SegmentSelected('MSH')
   */
  onHl7SegmentSelected(label: string): void {
    this.hl7PickerSegment = this.sanitizeHl7SegmentInput(label);
    this.hl7SegmentFilter$.next(this.hl7PickerSegment);
    this.commitHl7PickerModelToForm();
  }

  /**
   * Sanitizes segment on blur and commits picker model to the rule form.
   * @example hl7Rules.onHl7SegmentInputBlur()
   */
  onHl7SegmentInputBlur(): void {
    if (this.patchingHl7Form) {
      return;
    }
    const seg = this.sanitizeHl7SegmentInput(this.hl7PickerSegment);
    this.hl7PickerSegment = seg;
    this.commitHl7PickerModelToForm();
  }

  /**
   * Restricts index inputs to digits and updates picker model on field/component index change.
   * @example hl7Rules.onHl7IndexInput('hl7FieldIndex', inputEvent)
   */
  onHl7IndexInput(controlName: 'hl7FieldIndex' | 'hl7ComponentIndex', event: Event): void {
    const el = event.target as HTMLInputElement;
    const digits = (el.value ?? '').replace(/\D/g, '').slice(0, 2);
    if (el.value !== digits) {
      el.value = digits;
    }
    if (controlName === 'hl7FieldIndex') {
      this.hl7PickerFieldIndex = digits;
    } else {
      this.hl7PickerComponentIndex = digits;
    }
    this.onHl7PickerModelChanged();
  }

  /** Restricts sub-component index input to digits and syncs picker model. */
  onHl7SubComponentIndexInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    const digits = (el.value ?? '').replace(/\D/g, '').slice(0, 2);
    if (el.value !== digits) {
      el.value = digits;
    }
    this.store.ruleForm.get('hl7SubComponentIndex')?.setValue(digits, { emitEvent: false });
    this.onHl7PickerModelChanged();
  }

  /**
   * Commits index picks on blur; may auto-open Build Path after component when sub-component is skipped.
   * @example hl7Rules.onHl7IndexBlur('hl7ComponentIndex')
   */
  onHl7IndexBlur(
    controlName: 'hl7FieldIndex' | 'hl7ComponentIndex',
    options?: { openBuildPathOnComponent?: boolean },
    event?: FocusEvent
  ): void {
    if (this.patchingHl7Form) {
      return;
    }
    this.commitHl7PickerModelToForm();
    if (controlName === 'hl7ComponentIndex' && options?.openBuildPathOnComponent !== false) {
      this.scheduleHl7BuildPathAutoOpenAfterBlur(event);
    }
  }

  /** Commits sub-component and auto-opens Build Path once index entry is complete. */
  onHl7SubComponentIndexBlur(event?: FocusEvent): void {
    if (this.patchingHl7Form) {
      return;
    }
    this.commitHl7PickerModelToForm();
    this.scheduleHl7BuildPathAutoOpenAfterBlur(event);
  }

  /**
   * Builds display key from segment/indices and writes it to the field form control.
   * @example hl7Rules.syncHl7ControlsToField()
   */
  syncHl7ControlsToField(): void {
    if (this.patchingHl7Form) {
      return;
    }
    const seg = this.sanitizeHl7SegmentInput(
      this.hl7PickerSegment || ((this.store.ruleForm.get('hl7Segment')?.value ?? '') as string)
    );
    const fieldIdx = this.hl7IndexControlValue('hl7FieldIndex');
    const compIdx = this.hl7IndexControlValue('hl7ComponentIndex');
    const subIdx = this.hl7IndexControlValue('hl7SubComponentIndex');
    if (!seg || fieldIdx === '') {
      this.store.fieldControl.setValue('', { emitEvent: false });
      return;
    }
    const segOpt = this.catalog.getOptions().find((o) => o.label.toUpperCase() === seg);
    const built = buildHl7PathFromSegmentPicker({
      segment: seg,
      fieldIndex: fieldIdx,
      componentIndex: compIdx || null,
      subComponentIndex: subIdx || null,
      segmentBasePath: segOpt?.path ?? `HL7Message/${seg}`,
    });
    if (!built.displayKey) {
      return;
    }
    this.store.fieldControl.setValue(built.displayKey, { emitEvent: false });
    const existingMap = (this.store.fieldDisplayToPath.get(built.displayKey) ?? '').trim();
    if (!segOpt?.isRepeatable) {
      this.store.fieldDisplayToPath.set(built.displayKey, built.path);
    } else if (!existingMap.includes('[') && !built.path.includes('[')) {
      this.store.fieldDisplayToPath.set(built.displayKey, built.path);
    }
  }

  /**
   * Patches hl7Segment/indices form controls from picker properties and syncs field value.
   * @example hl7Rules.commitHl7PickerModelToForm()
   */
  commitHl7PickerModelToForm(): void {
    const seg = this.sanitizeHl7SegmentInput(this.hl7PickerSegment);
    this.hl7PickerSegment = seg;
    this.hl7PickerFieldIndex = (this.hl7PickerFieldIndex ?? '').toString().replace(/\D/g, '').slice(0, 2);
    this.hl7PickerComponentIndex = (this.hl7PickerComponentIndex ?? '')
      .toString()
      .replace(/\D/g, '')
      .slice(0, 2);
    const subIdx = this.hl7IndexControlValue('hl7SubComponentIndex');
    this.patchingHl7Form = true;
    this.store.ruleForm.patchValue(
      {
        hl7Segment: seg,
        hl7FieldIndex: this.hl7PickerFieldIndex,
        hl7ComponentIndex: this.hl7PickerComponentIndex,
        hl7SubComponentIndex: subIdx,
      },
      { emitEvent: false }
    );
    this.patchingHl7Form = false;
    this.syncHl7ControlsToField();
  }

  /**
   * True when current field/segment needs Build Path (repeatable or OBR/OBX locate).
   * @example hl7Rules.showBuildPathButton()
   */
  showBuildPathButton(): boolean {
    return this.hl7SupportsBuildPathContext();
  }

  /**
   * Opens Build Path dialog when repeatable segment or located persisted path applies.
   * @example hl7Rules.openBuildPathForCurrentField()
   */
  openBuildPathForCurrentField(): void {
    this.ensureLoaded();
    this.commitHl7PickerModelToForm();
    const key = ((this.store.fieldControl.value ?? '') || '').toString().trim();
    const seg = ((this.store.ruleForm.get('hl7Segment')?.value ?? '') as string)
      .toString()
      .trim()
      .toUpperCase();
    const segmentFromKey = key.includes('.') ? key.split('.')[0].toUpperCase() : key.toUpperCase();
    const repeatableSeg = seg || segmentFromKey;
    if (!key) {
      return;
    }
    const locate = this.resolveHl7ObrObxLocateForField(key, repeatableSeg);
    const persisted = this.resolveHl7PersistedPathForFieldLabel(key);
    const openForRepeatableOrLocated =
      (repeatableSeg && this.isHl7SegmentRepeatable(repeatableSeg)) ||
      (persisted && this.isHl7UserLocatedPersistedPath(persisted));
    const openForObrObxLocate = !!(locate?.obrFieldKeys?.length);
    if (openForRepeatableOrLocated || openForObrObxLocate) {
      this.openHl7BuildFieldPathDialog(false);
    }
  }

  /**
   * Converts persisted/rule field value to UI display key (e.g. HL7Message/... → OBX.5.1).
   * @example hl7Rules.toFieldDisplay(rule.field) // 'MSH.12'
   */
  toFieldDisplay(field: string): string {
    const raw = (field ?? '').toString().trim();
    if (!raw) {
      return '';
    }
    this.ensureLoaded();

    const hl7DisplayKey = hl7DisplayKeyFromPersistedPath(raw);
    if (hl7DisplayKey) {
      return hl7DisplayKey;
    }

    const fromReverseMap = this.findDisplayKeyForPersistedPath(raw);
    if (fromReverseMap && fromReverseMap !== raw && !this.isFullHl7PersistedPath(fromReverseMap)) {
      return fromReverseMap;
    }

    if (/^hl7message\//i.test(raw) || raw.includes('[@parentId')) {
      const hl7Key = this.getHl7KeyFromGeneratedPath(raw);
      if (hl7Key) {
        return hl7Key;
      }
    }

    const hl7FlatKey = this.findHl7FlattenedKeyByPersistedPath(raw);
    if (hl7FlatKey) {
      return hl7FlatKey;
    }
    const fromJson = this.catalog.findByPersistedPath(raw);
    if (fromJson) {
      return fromJson.label.trim();
    }
    const fromLabel = this.catalog.findByLabel(raw);
    if (fromLabel?.label) {
      return fromLabel.label;
    }
    if (this.store.fieldDisplayToPath.has(raw)) {
      return raw;
    }
    if (this.isLikelyHl7FieldKey(raw)) {
      return raw;
    }
    return raw;
  }

  /**
   * Converts display label to path stored on the rule (map, overrides, or generated HL7 path).
   * @example hl7Rules.toPersistedField('MSH.12') // 'HL7Message/MSH/MSH.12'
   */
  toPersistedField(displayLabel: string): string {
    const raw = (displayLabel ?? '').toString().trim();
    if (!raw) {
      return '';
    }
    const displayOverride = (this.store.fieldDisplayToPath.get(raw) ?? '').toString().trim();
    if (displayOverride) {
      return this.trimFieldPath(displayOverride);
    }
    if (this.catalog.findByPersistedPath(raw) && raw.includes('[@')) {
      return this.trimFieldPath(raw);
    }
    const meta = this.catalog.getMetaByKey(raw);
    if (meta?.path) {
      const generated = this.isLikelyHl7FieldKey(raw) ? this.buildHl7PathFromKey(raw) : '';
      if (generated && this.hl7FlatKeyDepth(raw) > this.hl7FlatKeyDepth(this.extractHl7FlatKeyFromStoredValue(meta.path))) {
        return this.trimFieldPath(generated);
      }
      return this.trimFieldPath(meta.path);
    }
    if (this.isLikelyHl7FieldKey(raw)) {
      return this.buildHl7PathFromKey(raw);
    }
    return raw;
  }

  /**
   * Validates HL7 picker, persists field path, and adds or updates the rule on the target block.
   * @example hl7Rules.saveAddOrUpdate()
   */
  saveAddOrUpdate(): void {
    this.onHl7SegmentInputBlur();
    this.onHl7IndexBlur('hl7FieldIndex', { openBuildPathOnComponent: false });
    this.onHl7IndexBlur('hl7ComponentIndex', { openBuildPathOnComponent: false });
    this.commitHl7PickerModelToForm();

    const seg = ((this.store.ruleForm.get('hl7Segment')?.value ?? '') as string).toString().trim();
    const fieldIdx = this.hl7IndexControlValue('hl7FieldIndex');
    if (!seg || fieldIdx === '') {
      this.toastr.error('Field segment and index are required (e.g. MSH and 12).', '', {
        timeOut: 4500,
        positionClass: 'toast-bottom-right',
      });
      return;
    }

    if (this.warnAndStopOnInvalidRuleName()) {
      return;
    }
    if (this.store.ruleForm.invalid) {
      return;
    }
    const block = this.getTargetBlockForSave();
    if (!block) {
      this.toastr.warning('Select or add a pipeline first.', '', {
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      });
      return;
    }

    const fieldValue = (this.store.fieldControl.value ?? '').toString().trim();
    if (
      !!fieldValue &&
      !this.isLikelyHl7FieldKey(fieldValue) &&
      !/^hl7message\//i.test(fieldValue)
    ) {
      this.toastr.error('Choose a valid HL7 segment and field indices (e.g. MSH.12.1).', '', {
        timeOut: 4500,
        positionClass: 'toast-bottom-right',
      });
      return;
    }

    const field = this.toPersistedField(fieldValue);
    if (!field) {
      return;
    }
    const displayKey = this.isLikelyHl7FieldKey(fieldValue)
      ? fieldValue
      : this.getHl7KeyFromGeneratedPath(fieldValue) || this.getHl7KeyFromGeneratedPath(field) || fieldValue;
    if (displayKey && field && displayKey !== field) {
      this.store.fieldDisplayToPath.set(displayKey, this.trimFieldPath(field));
    }

    const op = this.store.mapOperatorToUi(this.store.ruleForm.get('operator')?.value);
    const ruleName = this.store.getRuleNameInputValue();
    // const value = (this.store.ruleForm.get('value')?.value ?? '')
    //   .toString()
    //   .replace(/\s*\|\s*/g, '|')
    //   .trim();
    const value = this.ruleEngineService.normalizeRuleValue(
      this.store.ruleForm.get('value')?.value ?? ''
    );

    const excludeRuleIndex = this.store.isEditingAnyRule() ? this.store.editingRuleIndex : null;
    if (this.store.isDuplicateRuleNameInBlock(block, ruleName, excludeRuleIndex)) {
      this.toastr.warning(RULE_NAME_ALREADY_EXISTS_MESSAGE, '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right',
      });
      return;
    }

    if (this.store.isEditingAnyRule() && this.store.editingRuleIndex !== null) {
      const r = block.rules[this.store.editingRuleIndex];
      if (r) {
        r.rulename = ruleName;
        r.field = field;
        r.value = value;
        r.operation = op;
      }
    } else {
      const nextNo = block.rules.length + 1;
      block.rules.push({
        rulenumber: nextNo,
        rulename: ruleName || `Rule${nextNo}`,
        field,
        value,
        operation: op,
        delimiter: '|',
      });
      routingExpressionPrep.seedRuleSatisfactionIfFirstRule(block);
    }
    this.clearEditForm();
    this.store.patchBlocks([...this.store.pipelineBlocks]);
  }

  /**
   * Loads a rule into the form and HL7 picker, restoring display/path mappings.
   * @example hl7Rules.startEditRule(block.id, 0)
   */
  startEditRule(blockId: string, index: number): void {
    const block = this.store.pipelineBlocks.find((b) => b.id === blockId);
    const rule = block?.rules[index];
    if (!rule) {
      return;
    }
    this.ensureLoaded();
    this.store.editingBlockId = blockId;
    this.store.editingRuleIndex = index;
    this.store.isPipelineLocked = true;

    const persisted = (rule.field ?? '').toString();
    let display = this.toFieldDisplay(persisted);
    if (!display && /^hl7message\//i.test(persisted)) {
      display = this.getHl7KeyFromGeneratedPath(persisted) || '';
    }
    if (!display && this.isContextualHl7PersistedPath(persisted)) {
      display = this.getHl7KeyFromGeneratedPath(persisted) || '';
    }
    const pathForMap = /^hl7message\//i.test(persisted)
      ? persisted
      : this.isContextualHl7PersistedPath(persisted)
        ? persisted
        : this.buildHl7PathFromKey(display || persisted);
    if (display) {
      this.store.fieldDisplayToPath.set(display, (pathForMap || persisted).trim());
    }

    const parts = this.resolveHl7PickerPartsForEdit(persisted, display);
    const flatKey =
      (parts ? this.formatHl7DisplayKey(parts) : '') ||
      display ||
      this.extractHl7FlatKeyFromStoredValue(persisted);
    const fieldKey = parts ? this.formatHl7DisplayKey(parts) : flatKey || display || '';
    if (fieldKey) {
      this.store.fieldDisplayToPath.set(fieldKey, (pathForMap || persisted).trim());
    }

    this.store.ruleForm.patchValue(
      {
        pipeline: blockId,
        rulename: rule.rulename ?? '',
        operator: this.store.mapOperatorToUi(rule.operation ?? ''),
        value: this.ruleEngineService.normalizeRuleValue(rule.value ?? ''),
      },
      { emitEvent: true }
    );
    this.store.setRuleNameFieldDisabled(
      this.ruleEngineService.isRuleInBlockBuildExpression(rule, block)
    );
    this.writeHl7PickerModel(parts, fieldKey);
  }

  /**
   * Clears edit state and resets rule form and HL7 picker controls.
   * @example hl7Rules.clearEditForm()
   */
  clearEditForm(): void {
    this.store.editingBlockId = null;
    this.store.editingRuleIndex = null;
    this.store.isPipelineLocked = false;
    this.store.setRuleNameFieldDisabled(false);
    this.hl7BuildPathAutoOpenedForKey = '';
    const keepPipeline = (this.store.selectedPipelineId ?? '').toString();
    this.hl7PickerSegment = '';
    this.hl7PickerFieldIndex = '';
    this.hl7PickerComponentIndex = '';
    this.hl7SegmentFilter$.next('');
    this.patchingHl7Form = true;
    this.store.ruleForm.reset(
      {
        pipeline: keepPipeline,
        rulename: '',
        field: '',
        operator: '',
        value: '',
        hl7Segment: '',
        hl7FieldIndex: '',
        hl7ComponentIndex: '',
        hl7SubComponentIndex: '',
      },
      { emitEvent: false }
    );
    this.patchingHl7Form = false;
    this.store.ruleForm.markAsPristine();
    this.store.ruleForm.markAsUntouched();
  }

  /**
   * Subscribes to hl7Segment/indices form changes to sync field control and clear draft paths.
   * @example this.bindHl7FormSync() // once per service lifetime
   */
  private bindHl7FormSync(): void {
    if (this.formSyncBound) {
      return;
    }
    this.formSyncBound = true;
    this.store.ruleForm.get('hl7Segment')?.valueChanges.subscribe(() => {
      if (this.patchingHl7Form) {
        return;
      }
      this.hl7BuildPathAutoOpenedForKey = '';
      this.clearHl7DraftPathForCurrentField({ force: true });
      this.syncHl7ControlsToField();
    });
    this.store.ruleForm.get('hl7FieldIndex')?.valueChanges.subscribe(() => {
      if (this.patchingHl7Form) {
        return;
      }
      this.hl7BuildPathAutoOpenedForKey = '';
      this.clearHl7DraftPathForCurrentField();
      this.syncHl7ControlsToField();
    });
    this.store.ruleForm.get('hl7ComponentIndex')?.valueChanges.subscribe(() => {
      if (this.patchingHl7Form) {
        return;
      }
      this.clearHl7DraftPathForCurrentField();
      this.syncHl7ControlsToField();
    });
    this.store.ruleForm.get('hl7SubComponentIndex')?.valueChanges.subscribe(() => {
      if (this.patchingHl7Form) {
        return;
      }
      this.clearHl7DraftPathForCurrentField();
      this.syncHl7ControlsToField();
    });
  }

  /**
   * Sanitizes picker indices and commits model when picker properties change.
   * @example this.onHl7PickerModelChanged()
   */
  private onHl7PickerModelChanged(): void {
    if (this.patchingHl7Form) {
      return;
    }
    this.hl7PickerFieldIndex = (this.hl7PickerFieldIndex ?? '').toString().replace(/\D/g, '').slice(0, 2);
    this.hl7PickerComponentIndex = (this.hl7PickerComponentIndex ?? '')
      .toString()
      .replace(/\D/g, '')
      .slice(0, 2);
    this.commitHl7PickerModelToForm();
  }

  /**
   * Writes segment/indices and field key into picker properties and rule form.
   * @example this.writeHl7PickerModel({ segment: 'MSH', fieldIndex: '12', componentIndex: '' }, 'MSH.12')
   */
  private writeHl7PickerModel(
    parts: {
      segment: string;
      fieldIndex: string;
      componentIndex: string;
      subComponentIndex?: string;
    } | null,
    fieldKey: string
  ): void {
    this.hl7PickerSegment = (parts?.segment ?? '').toString().trim().toUpperCase();
    this.hl7SegmentFilter$.next(this.hl7PickerSegment);
    this.hl7PickerFieldIndex = parts ? this.hl7IndexToFormValue(parts.fieldIndex) : '';
    this.hl7PickerComponentIndex = parts ? this.hl7IndexToFormValue(parts.componentIndex) : '';
    const subComponentIndex = parts ? this.hl7IndexToFormValue(parts.subComponentIndex ?? '') : '';
    this.patchingHl7Form = true;
    this.store.ruleForm.patchValue(
      {
        hl7Segment: this.hl7PickerSegment,
        hl7FieldIndex: this.hl7PickerFieldIndex,
        hl7ComponentIndex: this.hl7PickerComponentIndex,
        hl7SubComponentIndex: subComponentIndex,
        field: (fieldKey ?? '').toString().trim(),
      },
      { emitEvent: false }
    );
    this.patchingHl7Form = false;
  }

  /**
   * Strips invalid characters and uppercases segment input to 2–4 alphanumeric chars.
   * @example this.sanitizeHl7SegmentInput('msh.12') // 'MSH'
   */
  private sanitizeHl7SegmentInput(raw: string): string {
    const cleaned = (raw || '').toString().trim().split(/[./\s]/)[0] ?? '';
    return cleaned.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 4);
  }

  /**
   * Parses display key or persisted path into segment, field index, and component index.
   * @example this.parseHl7FieldParts('OBX.5.1') // { segment: 'OBX', fieldIndex: '5', componentIndex: '1' }
   */
  private parseHl7FieldParts(value: string): {
    segment: string;
    fieldIndex: string;
    componentIndex: string;
    subComponentIndex: string;
  } | null {
    const raw = (value || '').toString().trim();
    if (!raw) {
      return null;
    }
    let key = raw;
    if (/^hl7message\//i.test(raw) || raw.includes('/')) {
      key = this.getHl7KeyFromGeneratedPath(raw) || '';
      if (!key && raw.includes('/')) {
        const tail = raw.split('/').map((p) => p.trim()).filter(Boolean).pop() || '';
        key = tail.includes('/') ? collapseHl7SlashPredicateAttributeToFlatKey(tail) : tail;
      }
    }
    if (!key) {
      return null;
    }
    key = key.replace(/\//g, '.');
    const m = /^([A-Za-z0-9]{2,4})\.(\d+)(?:\.(\d+))?(?:\.(\d+))?$/.exec(key);
    if (!m) {
      return null;
    }
    return {
      segment: m[1].toUpperCase(),
      fieldIndex: m[2],
      componentIndex: (m[3] ?? '').trim(),
      subComponentIndex: (m[4] ?? '').trim(),
    };
  }

  /**
   * Formats segment/indices into display key (e.g. MSH.12 or MSH.12.1).
   * @example this.formatHl7DisplayKey({ segment: 'MSH', fieldIndex: '12', componentIndex: '1' }) // 'MSH.12.1'
   */
  private formatHl7DisplayKey(parts: {
    segment: string;
    fieldIndex: string;
    componentIndex: string;
    subComponentIndex?: string;
  }): string {
    const seg = (parts.segment || '').trim().toUpperCase();
    const fi = (parts.fieldIndex || '').trim();
    const ci = (parts.componentIndex || '').trim();
    const sci = (parts.subComponentIndex || '').trim();
    if (!seg || !fi) {
      return '';
    }
    const numericParts = [fi];
    if (ci) {
      numericParts.push(ci);
    }
    if (sci && ci) {
      numericParts.push(sci);
    }
    return [seg, ...numericParts].join('.');
  }

  /**
   * Builds persisted slash path from flat HL7 display key via segment picker.
   * @example this.buildHl7PathFromKey('MSH.12') // 'HL7Message/MSH/MSH.12'
   */
  private buildHl7PathFromKey(rawKey: string): string {
    const key = (rawKey || '').toString().trim();
    if (!this.isLikelyHl7FieldKey(key)) {
      return key;
    }
    const parts = this.parseHl7FieldParts(key);
    if (!parts) {
      return key;
    }
    const segOpt = this.catalog
      .getOptions()
      .find((o) => o.label.toUpperCase() === parts.segment.toUpperCase());
    return buildHl7PathFromSegmentPicker({
      segment: parts.segment,
      fieldIndex: parts.fieldIndex,
      componentIndex: parts.componentIndex || null,
      subComponentIndex: parts.subComponentIndex || null,
      segmentBasePath: segOpt?.path ?? `HL7Message/${parts.segment}`,
    }).path;
  }

  /** Counts numeric index segments after the HL7 segment name (e.g. PID.3.2.2 → 3). */
  private hl7FlatKeyDepth(key: string): number {
    const m = /^[A-Za-z0-9]{2,4}\.([\d.]+)$/.exec((key || '').trim());
    if (!m) {
      return 0;
    }
    return m[1].split('.').filter(Boolean).length;
  }

  /**
   * True when value matches flat HL7 field key pattern (SEG.N or SEG.N.M).
   * @example this.isLikelyHl7FieldKey('PID.3.1') // true
   */
  private isLikelyHl7FieldKey(raw: string): boolean {
    return /^[A-Za-z0-9]{2,4}\.\d+(?:\.\d+)*$/.test((raw || '').trim());
  }

  /**
   * Opens Build Field Path dialog and applies result to field control and display map.
   * @example this.openHl7BuildFieldPathDialog(true)
   */
  private openHl7BuildFieldPathDialog(clearFieldOnCancel: boolean): void {
    this.commitHl7PickerModelToForm();
    const seg = ((this.store.ruleForm.get('hl7Segment')?.value ?? '') as string)
      .toString()
      .trim()
      .toUpperCase();
    let displayKey = ((this.store.fieldControl.value ?? '') as string).toString().trim();
    let initialPath = this.resolveHl7InitialPathForBuildDialog(displayKey);
    if (initialPath && displayKey) {
      this.store.fieldDisplayToPath.set(displayKey, this.trimFieldPath(initialPath));
    }
    if (!displayKey && initialPath && /^hl7message\//i.test(initialPath)) {
      displayKey = this.getHl7KeyFromGeneratedPath(initialPath) || '';
    }
    const parsed = displayKey ? this.parseHl7FieldParts(displayKey) : null;
    const fieldIdxStr = this.hl7IndexControlValue('hl7FieldIndex');
    const compIdxStr = this.hl7IndexControlValue('hl7ComponentIndex');
    const fieldIdx =
      parsed?.fieldIndex != null && `${parsed.fieldIndex}`.trim() !== ''
        ? this.hl7IndexFromParsedPart(parsed.fieldIndex)
        : fieldIdxStr === ''
          ? null
          : Number(fieldIdxStr);
    const compIdx =
      parsed?.componentIndex != null && `${parsed.componentIndex}`.trim() !== ''
        ? this.hl7IndexFromParsedPart(parsed.componentIndex)
        : compIdxStr === ''
          ? null
          : Number(compIdxStr);
    const subIdxStr = this.hl7IndexControlValue('hl7SubComponentIndex');
    const subIdx =
      parsed?.subComponentIndex != null && `${parsed.subComponentIndex}`.trim() !== ''
        ? this.hl7IndexFromParsedPart(parsed.subComponentIndex)
        : subIdxStr === ''
          ? null
          : Number(subIdxStr);

    this.dialog
      .open(BuildFieldPathDialogComponent, {
        width: '640px',
        maxWidth: '96vw',
        data: {
          hl7Picker: {
            segmentOptions: this.getHl7SegmentPickerOptions(),
            indexOptions: this.hl7IndexSelectOptions,
            targetSegment: seg,
            initialFieldIndex: fieldIdx,
            initialComponentIndex: compIdx,
            initialSubComponentIndex: subIdx,
            initialDisplayKey: displayKey,
            initialPath,
          },
        },
        disableClose: true,
        autoFocus: false,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: BuildFieldPathDialogResult | undefined) => {
        if (!result?.path?.trim()) {
          if (clearFieldOnCancel) {
            const key = ((this.store.fieldControl.value ?? '') as string).toString().trim();
            if (key) {
              this.store.fieldDisplayToPath.delete(key);
            }
            this.store.fieldControl.setValue('', { emitEvent: true });
            this.store.ruleForm.patchValue(
              { hl7Segment: '', hl7FieldIndex: '', hl7ComponentIndex: '', hl7SubComponentIndex: '' },
              { emitEvent: false }
            );
            this.writeHl7PickerModel(null, '');
          }
          return;
        }
        const resultKey = (result.displayKey ?? this.store.fieldControl.value ?? '').toString().trim();
        if (!resultKey) {
          return;
        }
        this.store.fieldDisplayToPath.set(resultKey, this.trimFieldPath(result.path.trim()));
        this.store.fieldControl.setValue(resultKey, { emitEvent: true });
        const parts = this.parseHl7FieldParts(resultKey);
        if (parts) {
          this.writeHl7PickerModel(parts, resultKey);
        }
      });
  }



  /**
   * Resolves OBR/OBX locate metadata for a field label and optional segment.
   * @example this.resolveHl7ObrObxLocateForField('OBX.5.1', 'OBX')?.obrFieldKeys
   */
  private resolveHl7ObrObxLocateForField(
    fieldLabel: string,
    segment?: string
  ): Hl7ObrObxLocateMeta | null {
    const key = (fieldLabel ?? '').trim();
    const seg = (segment ?? key.split('.')[0] ?? '').trim().toUpperCase();
    const meta = key ? this.catalog.getMetaByKey(key) : null;
    const locate = meta?.hl7ObrObxLocate ?? this.catalog.resolveRepeatLocate(seg, key);
    return locate?.obrFieldKeys?.length ? locate : null;
  }

  /**
   * Delegates repeatable check to catalog for segment or key prefix.
   * @example this.isHl7SegmentRepeatable('OBX') // true
   */
  private isHl7SegmentRepeatable(segmentOrKey: string): boolean {
    return this.catalog.isSegmentRepeatable(segmentOrKey);
  }

  /**
   * True when locate has OBR filter keys but no OBX filter keys.
   * @example this.isHl7ObrOnlyLocate(locate)
   */
  private isHl7ObrOnlyLocate(locate: Hl7ObrObxLocateMeta | null | undefined): boolean {
    return (locate?.obrFieldKeys?.length ?? 0) > 0 && !(locate?.obxFieldKeys?.length ?? 0);
  }

  /**
   * True when Build Path button should show for current field/segment context.
   * @example this.hl7SupportsBuildPathContext()
   */
  private hl7SupportsBuildPathContext(): boolean {
    const key = ((this.store.fieldControl.value ?? '') || '').toString().trim();
    const seg = ((this.store.ruleForm.get('hl7Segment')?.value ?? '') as string).toString().trim();
    if (key && this.isHl7SegmentRepeatable(key)) {
      return true;
    }
    if (seg && this.isHl7SegmentRepeatable(seg)) {
      return true;
    }
    if (key) {
      const metaHl7 = this.catalog.getMetaByKey(key);
      if ((metaHl7?.hl7ObrObxLocate?.obrFieldKeys?.length ?? 0) > 0) {
        return true;
      }
      const persisted =
        this.resolveHl7PersistedPathForFieldLabel(key) ||
        (this.store.fieldDisplayToPath.get(key) ?? '').trim();
      if (persisted && this.isHl7UserLocatedPersistedPath(persisted)) {
        return true;
      }
      if (/^hl7message\//i.test(key) || key.includes('[@parentId')) {
        return true;
      }
    }
    return false;
  }

  /**
   * Maps persisted path to display key via fieldDisplayToPath store.
   * @example this.findDisplayKeyForPersistedPath(savedPath) // 'OBX.2.2'
   */
  private findDisplayKeyForPersistedPath(persisted: string): string | null {
    const norm = this.trimFieldPath(persisted);
    if (!norm) {
      return null;
    }
    for (const [displayKey, path] of this.store.fieldDisplayToPath.entries()) {
      if (this.trimFieldPath(path) !== norm) {
        continue;
      }
      if (displayKey === norm || this.isFullHl7PersistedPath(displayKey)) {
        continue;
      }
      return displayKey;
    }
    return null;
  }

  /**
   * True when value is a full HL7 persisted path (not a flat display key).
   * @example this.isFullHl7PersistedPath('HL7Message/MSH/MSH.12') // true
   */
  private isFullHl7PersistedPath(value: string): boolean {
    const v = (value ?? '').toString().trim();
    if (!v) {
      return false;
    }
    if (/^hl7message\//i.test(v) || v.includes('[@parentId')) {
      return true;
    }
    return v.includes('/') && !isHl7PersistedFieldValue(v);
  }

  /**
   * Delegates persisted-path reverse lookup to catalog flattened map.
   * @example this.findHl7FlattenedKeyByPersistedPath(path) // 'OBX.5.1'
   */
  private findHl7FlattenedKeyByPersistedPath(persisted: string): string | null {
    return this.catalog.findFlattenedKeyByPersistedPath(persisted);
  }

  /**
   * Derives flat display key from generated HL7 persisted path.
   * @example this.getHl7KeyFromGeneratedPath('HL7Message/MSH/MSH.12') // 'MSH.12'
   */
  private getHl7KeyFromGeneratedPath(path: string): string | null {
    return hl7DisplayKeyFromPersistedPath(path);
  }

  /**
   * Hydrates HL7 picker from stored display or persisted path value.
   * @example this.patchHl7ControlsFromField('MSH.12', savedPath)
   */
  private patchHl7ControlsFromField(displayOrPath: string, persistedPath?: string): void {
    const parsed = this.resolveHl7PickerPartsFromStoredField(displayOrPath, persistedPath);
    if (!parsed) {
      return;
    }
    const displayKey = this.formatHl7DisplayKey(parsed);
    this.writeHl7PickerModel(
      {
        segment: parsed.segment,
        fieldIndex: this.hl7IndexToFormValue(parsed.fieldIndex),
        componentIndex: this.hl7IndexToFormValue(parsed.componentIndex),
        subComponentIndex: this.hl7IndexToFormValue(parsed.subComponentIndex),
      },
      displayKey
    );
    const pathHint = (persistedPath ?? displayOrPath ?? '').toString().trim();
    if (displayKey && pathHint) {
      const mapped = (this.store.fieldDisplayToPath.get(displayKey) ?? '').trim();
      if (!mapped || /^hl7message\//i.test(pathHint) || pathHint.includes('[@parentId')) {
        this.store.fieldDisplayToPath.set(displayKey, this.trimFieldPath(pathHint));
      }
    }
  }

  /**
   * Resolves picker segment/indices when editing an existing rule.
   * @example this.resolveHl7PickerPartsForEdit(persisted, display)
   */
  private resolveHl7PickerPartsForEdit(
    persisted: string,
    display: string
  ): {
    segment: string;
    fieldIndex: string;
    componentIndex: string;
    subComponentIndex: string;
  } | null {
    return (
      this.parseHl7PersistedPathToParts(persisted) ||
      (display ? this.parseHl7FieldParts(display) : null) ||
      this.resolveHl7PickerPartsFromStoredField(display || persisted, persisted)
    );
  }

  /**
   * Tries multiple candidates to parse picker parts from display or path strings.
   * @example this.resolveHl7PickerPartsFromStoredField(display, persisted)
   */
  private resolveHl7PickerPartsFromStoredField(
    displayOrPath: string,
    persistedPath?: string
  ): {
    segment: string;
    fieldIndex: string;
    componentIndex: string;
    subComponentIndex: string;
  } | null {
    const candidates = new Set<string>();
    const add = (v: string | null | undefined) => {
      const s = (v ?? '').toString().trim();
      if (s) {
        candidates.add(s);
      }
      const flat = this.extractHl7FlatKeyFromStoredValue(s);
      if (flat) {
        candidates.add(flat);
      }
    };
    add(persistedPath);
    add(displayOrPath);
    add(this.toFieldDisplay(persistedPath ?? ''));
    add(this.toFieldDisplay(displayOrPath));
    for (const c of candidates) {
      const parsed = this.parseHl7FieldParts(c);
      if (parsed) {
        return parsed;
      }
    }
    return null;
  }

  /**
   * Parses persisted HL7 path into segment/field/component parts for the picker.
   * @example this.parseHl7PersistedPathToParts(rule.field)
   */
  private parseHl7PersistedPathToParts(persisted: string): {
    segment: string;
    fieldIndex: string;
    componentIndex: string;
    subComponentIndex: string;
  } | null {
    const raw = (persisted ?? '').toString().trim();
    if (!raw) {
      return null;
    }
    const candidates = [
      raw,
      this.extractHl7FlatKeyFromStoredValue(raw),
      this.getHl7KeyFromGeneratedPath(raw) ?? '',
      this.toFieldDisplay(raw),
    ].filter((c) => !!c);
    for (const candidate of candidates) {
      const parsed = this.parseHl7FieldParts(candidate);
      if (parsed) {
        return parsed;
      }
    }
    return null;
  }

  /** Waits for focus to settle, then auto-opens Build Path only when leaving the HL7 index picker. */
  private scheduleHl7BuildPathAutoOpenAfterBlur(event?: FocusEvent): void {
    const relatedTarget = (event?.relatedTarget as HTMLElement | null) ?? null;
    requestAnimationFrame(() => {
      if (this.isFocusInsideHl7FieldPicker(relatedTarget)) {
        return;
      }
      const active = document.activeElement as HTMLElement | null;
      if (this.isFocusInsideHl7FieldPicker(active)) {
        return;
      }
      this.maybeAutoOpenHl7BuildPath();
    });
  }

  private isFocusInsideHl7FieldPicker(el: HTMLElement | null): boolean {
    return !!el?.closest('.routing-hl7-field-shell');
  }

  /**
   * Auto-opens Build Path when index entry is complete on a repeatable segment (new rules only).
   * @example this.maybeAutoOpenHl7BuildPath()
   */
  private maybeAutoOpenHl7BuildPath(): void {
    if (this.store.isEditingAnyRule()) {
      return;
    }
    const seg = ((this.store.ruleForm.get('hl7Segment')?.value ?? '') as string)
      .toString()
      .trim()
      .toUpperCase();
    if (!seg || !this.isHl7SegmentRepeatable(seg)) {
      return;
    }
    const fieldIdx = this.hl7IndexControlValue('hl7FieldIndex');
    if (!fieldIdx) {
      return;
    }
    const compIdx = this.hl7IndexControlValue('hl7ComponentIndex');
    const subIdx = this.hl7IndexControlValue('hl7SubComponentIndex');
    const displayKey = this.formatHl7DisplayKey({
      segment: seg,
      fieldIndex: fieldIdx,
      componentIndex: compIdx,
      subComponentIndex: subIdx,
    });
    const guardKey = displayKey || `${seg}.${fieldIdx}.${compIdx || '_'}.${subIdx || '_'}`;
    if (this.hl7BuildPathAutoOpenedForKey === guardKey) {
      return;
    }
    this.hl7BuildPathAutoOpenedForKey = guardKey;
    this.openHl7BuildFieldPathDialog(true);
  }

  /**
   * Delegates path normalization to catalog.
   * @example this.trimFieldPath(' /HL7Message/MSH ')
   */
  private trimFieldPath(path: string): string {
    return this.catalog.trimFieldPath(path);
  }

  /**
   * Removes draft display→path mapping when segment/indices change (unless user-located path).
   * @example this.clearHl7DraftPathForCurrentField({ force: true })
   */
  private clearHl7DraftPathForCurrentField(options?: { force?: boolean }): void {
    const key = ((this.store.fieldControl.value ?? '') as string).toString().trim();
    if (!key) {
      return;
    }
    const mapped = (this.store.fieldDisplayToPath.get(key) ?? '').trim();
    if (!options?.force && mapped && this.isHl7UserLocatedPersistedPath(mapped)) {
      return;
    }
    this.store.fieldDisplayToPath.delete(key);
  }

  /**
   * True when path was set via Build Path (predicates or brackets).
   * @example this.isHl7UserLocatedPersistedPath(savedPath)
   */
  private isHl7UserLocatedPersistedPath(path: string): boolean {
    const p = (path || '').trim();
    if (!p || !/^hl7message\//i.test(p)) {
      return false;
    }
    return this.isContextualHl7PersistedPath(p) || p.includes('[');
  }

  /**
   * True for OBR-context or @parentId HL7 paths.
   * @example this.isContextualHl7PersistedPath(path)
   */
  private isContextualHl7PersistedPath(path: string): boolean {
    const p = (path || '').trim();
    return p.includes('[@parentId') || /^HL7Message\/OBR\[/i.test(p);
  }

  /**
   * Resolves persisted path for a display label from map, alt keys, or editing rule.
   * @example this.resolveHl7PersistedPathForFieldLabel('OBX.5.1')
   */
  private resolveHl7PersistedPathForFieldLabel(fieldLabel: string): string {
    const key = (fieldLabel ?? '').toString().trim();
    if (!key) {
      return '';
    }
    const fromMap = (this.store.fieldDisplayToPath.get(key) ?? '').trim();
    if (fromMap) {
      return fromMap;
    }
    const parts = this.parseHl7FieldParts(key);
    if (parts) {
      const alt = this.formatHl7DisplayKey(parts);
      const fromAlt = (this.store.fieldDisplayToPath.get(alt) ?? '').trim();
      if (fromAlt) {
        return fromAlt;
      }
    }
    return this.getHl7PersistedPathFromEditingRule();
  }

  /**
   * Returns contextual persisted path from the rule currently being edited.
   * @example this.getHl7PersistedPathFromEditingRule()
   */
  private getHl7PersistedPathFromEditingRule(): string {
    if (!this.store.isEditingAnyRule()) {
      return '';
    }
    const block = this.store.pipelineBlocks.find((b) => b.id === this.store.editingBlockId);
    const rule = block?.rules?.[this.store.editingRuleIndex ?? -1];
    const persisted = (rule?.field ?? '').toString().trim();
    if (!persisted) {
      return '';
    }
    if (
      /^hl7message\//i.test(persisted) ||
      this.isContextualHl7PersistedPath(persisted) ||
      this.isHl7UserLocatedPersistedPath(persisted)
    ) {
      return this.trimFieldPath(persisted);
    }
    return '';
  }

  /**
   * Resolves initial persisted path passed into Build Path dialog.
   * @example this.resolveHl7InitialPathForBuildDialog('OBX.5.1')
   */
  private resolveHl7InitialPathForBuildDialog(displayKey: string): string {
    const fromLabel = this.resolveHl7PersistedPathForFieldLabel(displayKey);
    if (fromLabel) {
      return fromLabel;
    }
    if (displayKey && this.isContextualHl7PersistedPath(displayKey)) {
      return displayKey.trim();
    }
    if (displayKey && this.isLikelyHl7FieldKey(displayKey)) {
      const built = this.buildHl7PathFromKey(displayKey);
      if (built && /^hl7message\//i.test(built)) {
        return built;
      }
    }
    return '';
  }

  /**
   * Maps catalog segment options to Build Path dialog segment picker shape.
   * @example this.getHl7SegmentPickerOptions()
   */
  private getHl7SegmentPickerOptions(): Hl7SegmentPickerOption[] {
    this.ensureLoaded();
    return this.catalog.getOptions().map((o) => ({
      label: o.label,
      path: o.path,
      isRepeatable: o.isRepeatable,
    }));
  }



  /**
   * Extracts flat HL7 key from display key, generated path, or path tail.
   * @example this.extractHl7FlatKeyFromStoredValue(rule.field) // 'OBX.5.1'
   */
  private extractHl7FlatKeyFromStoredValue(value: string): string {
    const raw = (value || '').toString().trim();
    if (!raw) {
      return '';
    }
    if (this.isLikelyHl7FieldKey(raw)) {
      return raw;
    }
    const fromGen = this.getHl7KeyFromGeneratedPath(raw);
    if (fromGen && this.isLikelyHl7FieldKey(fromGen)) {
      return fromGen;
    }
    const tail = raw.includes('/') ? raw.split('/').map((p) => p.trim()).filter(Boolean).pop() || '' : raw;
    if (!tail) {
      return '';
    }
    const flat = tail.includes('/')
      ? collapseHl7SlashPredicateAttributeToFlatKey(tail)
      : tail;
    if (this.isLikelyHl7FieldKey(flat)) {
      return flat;
    }
    const m = /([A-Za-z][A-Za-z0-9]{1,3})\.(\d+)(?:\.(\d+))?/.exec(flat);
    if (m) {
      return m[3] ? `${m[1].toUpperCase()}.${m[2]}.${m[3]}` : `${m[1].toUpperCase()}.${m[2]}`;
    }
    return '';
  }

  /**
   * Reads sanitized 0–99 index string from form control or picker property.
   * @example this.hl7IndexControlValue('hl7FieldIndex') // '12'
   */
  private hl7IndexControlValue(
    controlName: 'hl7FieldIndex' | 'hl7ComponentIndex' | 'hl7SubComponentIndex'
  ): string {
    const picker =
      controlName === 'hl7FieldIndex'
        ? this.hl7PickerFieldIndex
        : controlName === 'hl7ComponentIndex'
          ? this.hl7PickerComponentIndex
          : '';
    const raw = this.store.ruleForm.get(controlName)?.value ?? picker;
    const digits = (raw ?? '').toString().replace(/\D/g, '');
    if (digits === '') {
      return '';
    }
    const n = parseInt(digits, 10);
    if (!Number.isFinite(n) || n < 0 || n > 99) {
      return '';
    }
    return `${n}`;
  }

  /**
   * Parses index part string to number or null if invalid.
   * @example this.hl7IndexFromParsedPart('05') // 5
   */
  private hl7IndexFromParsedPart(part: string): number | null {
    const s = (part ?? '').trim();
    if (s === '') {
      return null;
    }
    const n = Math.floor(Number(s));
    return Number.isFinite(n) && n >= 0 && n <= 99 ? n : null;
  }

  /**
   * Converts parsed index part to form control string value.
   * @example this.hl7IndexToFormValue('3') // '3'
   */
  private hl7IndexToFormValue(part: string): string {
    const n = this.hl7IndexFromParsedPart(part);
    return n === null ? '' : `${n}`;
  }

  /**
   * Coerces autocomplete/object field control values to display string.
   * @example this.normalizeFieldControlValue({ path: 'HL7Message/MSH/MSH.12' }) // 'MSH.12'
   */
  private normalizeFieldControlValue(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'string') {
      return null;
    }
    const obj = value as { path?: string; value?: string; label?: string; key?: string; leaf?: string };
    const rawPath = (obj.path ?? obj.value ?? '').toString().trim();
    if (rawPath) {
      return this.toFieldDisplay(rawPath);
    }
    const label = (obj.label ?? obj.key ?? obj.leaf ?? '').toString().trim();
    if (label) {
      return label;
    }
    return `${value}`;
  }

  /**
   * Resolves pipeline block for add/update (editing block or selected pipeline, creating if needed).
   * @example this.getTargetBlockForSave()?.rules
   */
  private getTargetBlockForSave(): RoutingPipelineBlock | null {
    if (this.store.editingBlockId !== null && this.store.editingRuleIndex !== null) {
      return this.store.pipelineBlocks.find((b) => b.id === this.store.editingBlockId) ?? null;
    }
    const pipelineId = (
      this.store.selectedPipelineId ??
      ((this.store.ruleForm.get('pipeline')?.value ?? '') || '').toString()
    )
      .toString()
      .trim();
    if (!pipelineId) {
      return null;
    }
    let block = this.store.pipelineBlocks.find((b) => b.id === pipelineId);
    if (!block) {
      block = {
        id: pipelineId,
        label: this.store.pipelineIdToDisplayName.get(pipelineId) || pipelineId,
        rules: [],
        ruleSatisfaction: '1',
        expanded: true,
      };
      this.store.pipelineBlocks.push(block);
    }
    return block;
  }

  private warnAndStopOnInvalidRuleName(): boolean {
    const control = this.store.ruleForm.get('rulename');
    control?.markAsTouched();
    const message = getRuleNameValidationMessage(this.store.getRuleNameInputValue());
    if (!message) {
      return false;
    }
    this.toastr.warning(message, '', {
      timeOut: 4000,
      positionClass: 'toast-bottom-right',
    });
    return true;
  }
}
